// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// API servisi
import { Tenant } from "services/api/tenants/types";
import { getTenantAPI } from "services/api/tenants/api";
import { getActivityLogsAPI } from "services/api/fleet/vehicles/activitylogs/api";
import {
  ActivityLogRow,
  ActivityLogTypeEnum,
} from "services/api/fleet/vehicles/activitylogs/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// Tipovi i konstante
import { TenantActivitiesPageCmp, TenantActivitiesPageProps } from "./types";
import {
  TENANT_DETAIL_PAGE_TITLE,
  TENANT_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { TableSearch } from "ui/components/Table/TableSearch";
import UserCell from "ui/components/Table/TableSearch/components/Cells/UserCell";
import Badge from "ui/components/Badge/Badge";
import { BadgeProps } from "ui/components/Badge/types";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import TabMenu from "../TabMenu/TabMenu";

const TYPE_VARIANT: Record<number, BadgeProps["variant"]> = {
  [ActivityLogTypeEnum.Create]: "success",
  [ActivityLogTypeEnum.Update]: "info",
  [ActivityLogTypeEnum.Delete]: "danger",
  [ActivityLogTypeEnum.Import]: "violet",
  [ActivityLogTypeEnum.Publish]: "success",
};

const formatDateTime = (iso: string): string =>
  new Intl.DateTimeFormat("hr-HR", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso)
  );

const ACTIVITY_COLUMNS: Column[] = [
  {
    title: "Korisnik",
    field: "userFullName",
    type: ColumnTypes.custom,
    render: (row: ActivityLogRow) => <UserCell fullName={row.userFullName} userId={row.userId} />,
  },
  {
    title: "Tip",
    field: "activityLogTypeName",
    type: ColumnTypes.custom,
    render: (row: ActivityLogRow) =>
      row.activityLogTypeName ? (
        <Badge
          text={row.activityLogTypeName}
          variant={TYPE_VARIANT[row.activityLogType] ?? "secondary"}
          height="small"
        />
      ) : (
        <></>
      ),
  },
  {
    title: "Aktivnost",
    field: "comment",
    type: ColumnTypes.custom,
    render: (row: ActivityLogRow) => (
      <div>
        <div className="text-slate-800">
          {row.comment || row.entityDescription || row.entityName}
        </div>
        {row.entityName && (row.comment || row.entityDescription) && (
          <div className="text-xs text-slate-400">{row.entityName}</div>
        )}
      </div>
    ),
  },
  {
    title: "Datum",
    field: "created",
    type: ColumnTypes.custom,
    render: (row: ActivityLogRow) => (
      <span className="text-slate-500 text-sm whitespace-nowrap">{formatDateTime(row.created)}</span>
    ),
  },
];

const TenantActivitiesPage: TenantActivitiesPageCmp = (
  _props: TenantActivitiesPageProps
) => {
  const { tenantId } = useParams<{ tenantId: string }>();

  const [tenant, setTenant] = useState<Tenant>();
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ActivityLogRow>
  >(initialDataWithPagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchTenant = useCallback(async () => {
    if (!tenantId) return;
    try {
      setTenant(await getTenantAPI(tenantId));
    } catch {
      // error handled by snackbar
    }
  }, [tenantId]);

  const fetchActivities = useCallback(
    async (pagination: Pagination) => {
      if (!tenantId) return;
      setIsLoading(true);
      try {
        setSearchResults(await getActivityLogsAPI(pagination, { tenantId }));
      } catch {
        setSearchResults(initialDataWithPagination);
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId]
  );

  useEffect(() => {
    fetchTenant();
    fetchActivities(initialDataWithPagination.pagination);
  }, [fetchTenant, fetchActivities]);

  const handlePaginationChange = (pagination: Pagination) => {
    fetchActivities(pagination);
  };

  const title = TENANT_DETAIL_PAGE_TITLE;
  const breadcrumbs: BreadcrumbItem[] = [
    { text: TENANT_SEARCH_PAGE_TITLE.text, route: TENANT_SEARCH_PAGE_TITLE.route },
  ];

  const tenantName = tenant
    ? tenant.companyName || [tenant.firstName, tenant.lastName].filter(Boolean).join(" ")
    : "";

  if (!tenantId) return <></>;

  return (
    <div className="">
      <div className="bg-white rounded-t-md">
        <PageHeader>
          <PageTitle
            title={tenantName ? `${title.text} [${tenantName}]` : title.text}
            icon={title.icon}
            iconColor={title.iconColor}
          />
          <Breadcrumb items={breadcrumbs} />
        </PageHeader>
        <TabMenu tenantId={tenantId} />
      </div>
      <div className="px-4 py-6">
        <div className="bg-white border border-slate-200 rounded-sm">
     
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema zabilježenih aktivnosti za ovog tenanta" />
          ) : (
            <TableSearch
              columns={ACTIVITY_COLUMNS}
              data={searchResults.data}
              selectableRows={false}
              pagination={searchResults.pagination}
              onPaginationChange={handlePaginationChange}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

TenantActivitiesPage.route = Routes.TenantActivitiesPageRouter;

export default TenantActivitiesPage;
